import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Headphones, 
  Heart, 
  Target, 
  CheckCircle, 
  Phone,
  MessageCircle,
  FileText,
  Users,
  Brain,
  Shield,
  Star,
  ArrowRight,
  Euro,
  Video,
  MapPin,
  User,
  Users2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Monitor,
  Home,
  Globe,
  ChevronLeft,
  ChevronRight,
  Send
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEO/SEOHead';
import Services from '../components/sections/Services';
import BookingCalendar from '../components/ui/BookingCalendar';
import PhoneInput from '../components/ui/PhoneInput';
import { useTranslation } from '../hooks/useTranslation';

export default function ProcessPage() {
  const { t, currentLanguage } = useTranslation();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  
  const [openFAQ, setOpenFAQ] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+49',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setShowForm(true);
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const steps = [
    {
      icon: Phone,
      title: t('method.steps.step1.title'),
      duration: t('method.steps.step1.duration'),
      description: t('method.steps.step1.description'),
      details: [
        t('method.steps.step1.details.contact'),
        t('method.steps.step1.details.readiness'),
        t('method.steps.step1.details.organization')
      ]
    },
    {
      icon: MessageCircle,
      title: t('method.steps.step2.title'),
      duration: t('method.steps.step2.duration'),
      description: t('method.steps.step2.description'),
      details: [
        t('method.steps.step2.details.analysis'),
        t('method.steps.step2.details.goals'),
        t('method.steps.step2.details.adaptation')
      ]
    },
    {
      icon: FileText,
      title: t('method.steps.step3.title'),
      duration: t('method.steps.step3.duration'),
      description: t('method.steps.step3.description'),
      details: [
        t('method.steps.step3.details.plan'),
        t('method.steps.step3.details.sessions'),
        t('method.steps.step3.details.structure')
      ]
    },
    {
      icon: CheckCircle,
      title: t('method.steps.step4.title'),
      duration: t('method.steps.step4.duration'),
      description: t('method.steps.step4.description'),
      details: [
        t('method.steps.step4.details.decision'),
        t('method.steps.step4.details.payment'),
        t('method.steps.step4.details.planning')
      ]
    }
  ];

  const preparationSteps = [
    {
      icon: Clock,
      title: t('process.preparation.time.title'),
      description: t('process.preparation.time.description')
    },
    {
      icon: Headphones,
      title: t('process.preparation.equipment.title'),
      description: t('process.preparation.equipment.description')
    },
    {
      icon: Heart,
      title: t('process.preparation.mindset.title'),
      description: t('process.preparation.mindset.description')
    },
    {
      icon: FileText,
      title: t('process.preparation.documents.title'),
      description: t('process.preparation.documents.description')
    }
  ];



  // Цены на услуги
  const pricing = [
    {
      title: 'Первичная консультация',
      duration: '30 минут',
      price: 'Бесплатно',
      description: 'Знакомство, анализ запроса, рекомендации',
      features: [
        'Анализ вашего запроса',
        'Обсуждение целей',
        'Рекомендации по работе',
        'Ответы на вопросы'
      ],
      popular: false
    },
    {
      title: 'Индивидуальная сессия',
      duration: '75-90 минут',
      price: '€120',
      description: 'Полная гипнотерапевтическая сессия',
      features: [
        'Предварительное обсуждение',
        'Гипнотическая сессия',
        'Интеграция результатов',
        'Домашние задания'
      ],
      popular: true
    },
    {
      title: 'Пакет из 3 сессий',
      duration: '3 × 75-90 минут',
      price: '€320',
      originalPrice: '€360',
      description: 'Комплексная работа над проблемой',
      features: [
        '3 полные сессии',
        'Между сессиями поддержка',
        'Отслеживание прогресса',
        'Скидка 11%'
      ],
      popular: false
    },
    {
      title: 'Полная программа',
      duration: '6-8 сессий',
      price: '€600',
      originalPrice: '€720',
      description: 'Глубокое решение проблемы',
      features: [
        '6-8 сессий по потребности',
        'Персональная программа',
        'Неограниченная поддержка',
        'Скидка 17%'
      ],
      popular: false
    }
  ];

  // FAQ
  const faqData = t('faq.questions', { returnObjects: true });

  // Форматы работы
  const workFormats = [
    {
      icon: Monitor,
      title: t('work_formats.online.title'),
      description: t('work_formats.online.description'),
      features: [
        t('work_formats.online.features.video_calls'),
        t('work_formats.online.features.time_saving'),
        t('work_formats.online.features.comfort')
      ],
      pros: [
        t('work_formats.online.pros.convenience'),
        t('work_formats.online.pros.time_efficiency'),
        t('work_formats.online.pros.comfortable_setting'),
        t('work_formats.online.pros.flexible_schedule')
      ],
      cons: [
        t('work_formats.online.cons.internet_required'),
        t('work_formats.online.cons.quiet_place')
      ]
    },
    {
      icon: MapPin,
      title: t('work_formats.offline.title'),
      description: t('work_formats.offline.description'),
      features: [
        t('work_formats.offline.features.personal_contact'),
        t('work_formats.offline.features.comfortable_office'),
        t('work_formats.offline.features.full_concentration'),
        t('work_formats.offline.features.professional_atmosphere')
      ],
      pros: [
        t('work_formats.offline.pros.personal_contact'),
        t('work_formats.offline.pros.full_concentration'),
        t('work_formats.offline.pros.professional_setting'),
        t('work_formats.offline.pros.no_tech_issues')
      ],
      cons: [
        t('work_formats.offline.cons.travel_time'),
        t('work_formats.offline.cons.less_flexible')
      ]
    },
    {
      icon: User,
      title: t('work_formats.individual.title'),
      description: t('work_formats.individual.description'),
      features: [
        t('work_formats.individual.features.full_attention'),
        t('work_formats.individual.features.individual_approach'),
        t('work_formats.individual.features.method_flexibility'),
        t('work_formats.individual.features.maximum_effectiveness')
      ],
      pros: [
        t('work_formats.individual.pros.personal_approach'),
        t('work_formats.individual.pros.maximum_effectiveness'),
        t('work_formats.individual.pros.method_flexibility'),
        t('work_formats.individual.pros.full_privacy')
      ],
      cons: [
        t('work_formats.individual.cons.higher_cost'),
        t('work_formats.individual.cons.limited_availability')
      ]
    },
    {
      icon: Users2,
      title: t('work_formats.group.title'),
      description: t('work_formats.group.description'),
      features: [
        t('work_formats.group.features.self_hypnosis_training'),
        t('work_formats.group.features.self_practice_training'),
        t('work_formats.group.features.group_support'),
        t('work_formats.group.features.cost_saving'),
        t('work_formats.group.features.experience_sharing')
      ],
      pros: [
        t('work_formats.group.pros.cost_saving'),
        t('work_formats.group.pros.group_support'),
        t('work_formats.group.pros.experience_sharing'),
        t('work_formats.group.pros.regular_meetings')
      ],
      cons: [
        t('work_formats.group.cons.less_individuality'),
        t('work_formats.group.cons.less_deep_approach')
      ]
    }
   ];

   // Функции для календаря
   const handleInputChange = (e) => {
     const { name, value } = e.target;
     setFormData(prev => ({
       ...prev,
       [name]: value
     }));
   };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          selectedDate,
          selectedTime,
          language: currentLanguage
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        console.error('Ошибка при отправке формы:', result.error);
        alert(result.error || 'Произошла ошибка при отправке формы');
      }
    } catch (error) {
      console.error('Ошибка сети:', error);
      alert('Ошибка сети. Пожалуйста, попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
    
    // Сброс формы через 5 секунд
    setTimeout(() => {
      setIsSubmitted(false);
      setShowForm(false);
      setSelectedDate('');
      setSelectedTime('');
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '+49',
        message: '',
        service: ''
      });
    }, 5000);
  };

   const goToPreviousMonth = () => {
     setCurrentMonth(prev => {
       const newMonth = new Date(prev);
       newMonth.setMonth(prev.getMonth() - 1);
       return newMonth;
     });
   };

   const goToNextMonth = () => {
     setCurrentMonth(prev => {
       const newMonth = new Date(prev);
       newMonth.setMonth(prev.getMonth() + 1);
       return newMonth;
     });
   };

   const getDaysInMonth = (date) => {
     const year = date.getFullYear();
     const month = date.getMonth();
     const firstDay = new Date(year, month, 1);
     const lastDay = new Date(year, month + 1, 0);
     const daysInMonth = lastDay.getDate();
     const startingDay = firstDay.getDay();
     
     const days = [];
     
     // Добавляем пустые дни в начале месяца
     for (let i = 0; i < startingDay; i++) {
       days.push(null);
     }
     
     // Добавляем дни месяца
     for (let i = 1; i <= daysInMonth; i++) {
       days.push(new Date(year, month, i));
     }
     
     return days;
   };

   const isDateAvailable = (date) => {
     if (!date) return false;
     const today = new Date();
     today.setHours(0, 0, 0, 0);
     return date >= today;
   };

   // Доступное время
   const availableTimes = [
     '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'
   ];

   return (
    <div className="min-h-screen bg-white text-dark-900">
      <SEOHead 
        title={t('process.seo.title')}
        description={t('process.seo.description')}
        keywords={t('process.seo.keywords')}
      />
      <Header />
      <main>
        {/* Основные этапы */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-dark-900">
                {t('method.steps.title')}
              </motion.h2>
              <motion.p variants={item} className="text-base sm:text-lg max-w-3xl mx-auto text-gray-600">
                {t('method.steps.subtitle')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mb-16 sm:mb-20"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {steps.map((step, index) => (
                  <motion.div
                    key={index}
                    variants={item}
                    className="card-elevated p-6 sm:p-8 relative"
                  >
                    {/* Номер этапа */}
                    <div className="absolute top-4 right-4 w-10 h-10 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center text-lg font-medium">
                      {index + 1}
                    </div>

                    {/* Иконка и заголовок */}
                    <div className="flex items-center gap-3 sm:gap-4 mb-4">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 rounded-2xl flex items-center justify-center">
                        <step.icon className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600" />
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-dark-900">{step.title}</h3>
                        <p className="text-rose-600 font-medium text-sm sm:text-base">{step.duration}</p>
                      </div>
                    </div>

                    {/* Описание */}
                    <p className="text-gray-700 mb-4 leading-relaxed text-sm sm:text-base">
                      {step.description}
                    </p>

                    {/* Детали */}
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>


        {/* Подготовка к сессии */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-dark-900">
                {t('process.preparation.title')}
              </motion.h2>
              <motion.p variants={item} className="text-base sm:text-lg max-w-3xl mx-auto text-gray-600">
                {t('process.preparation.subtitle')}
              </motion.p>
            </motion.div>
            
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16"
            >
              {preparationSteps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="card-elevated p-4 sm:p-6 text-center"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <step.icon className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold mb-2 text-dark-900">{step.title}</h4>
                  <p className="text-gray-600 text-xs sm:text-sm">{step.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>


        {/* Услуги и пакеты */}
        <Services />

        {/* Форматы работы */}
        <section className="py-16 sm:py-20 bg-black text-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-white">
                {t('work_formats.title')}
              </motion.h2>
              <motion.p variants={item} className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto">
                {t('work_formats.subtitle')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16"
            >
              {workFormats.map((format, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="bg-white/10 border border-white/20 rounded-3xl p-6 sm:p-8 hover:bg-white/15 transition-all duration-300 shadow-xl"
                >
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-rose-100 rounded-2xl flex items-center justify-center">
                      <format.icon className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-white">{format.title}</h3>
                      <p className="text-gray-300 text-sm sm:text-base">{format.description}</p>
                    </div>
                  </div>

                  {/* Особенности */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="font-semibold mb-2 sm:mb-3 text-white text-sm sm:text-base">{t('work_formats.what_included')}</h4>
                    <ul className="space-y-1 sm:space-y-2">
                      {format.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2 text-xs sm:text-sm text-gray-300">
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Плюсы и минусы */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <h4 className="font-semibold mb-2 text-rose-400 text-sm sm:text-base">{t('work_formats.advantages')}</h4>
                      <ul className="space-y-1">
                        {format.pros.map((pro, proIndex) => (
                          <li key={proIndex} className="text-xs sm:text-sm text-gray-300 flex items-start gap-1">
                            <span className="text-rose-500">✓</span>
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 text-gray-400 text-sm sm:text-base">{t('work_formats.considerations')}</h4>
                      <ul className="space-y-1">
                        {format.cons.map((con, conIndex) => (
                          <li key={conIndex} className="text-xs sm:text-sm text-gray-400 flex items-start gap-1">
                            <span className="text-gray-500">!</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <motion.h2 variants={item} className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-dark-900">
                {t('faq.title')}
              </motion.h2>
              <motion.p variants={item} className="text-base sm:text-lg max-w-3xl mx-auto text-gray-600">
                {t('faq.subtitle')}
              </motion.p>
            </motion.div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <motion.div
                    key={index}
                    variants={item}
                    className="card-elevated overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                      className="w-full p-4 sm:p-6 text-left flex items-center justify-between hover:bg-white transition-colors"
                    >
                      <h3 className="text-base sm:text-lg font-semibold text-dark-900 pr-4">{faq.question}</h3>
                      {openFAQ === index ? (
                        <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    
                    {openFAQ === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-4 sm:px-6 pb-4 sm:pb-6"
                      >
                        <div className="pt-3 sm:pt-4 border-t border-gray-200">
                          <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Дополнительная помощь */}
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center mt-12 sm:mt-16"
            >
              <motion.div variants={item} className="bg-blue-50 rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto">
                <HelpCircle className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-dark-900">
                  {t('help_section.title')}
                </h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                  {t('help_section.description')}
                </p>
                <a
                  href={getLocalizedPath("/contact")}
                  className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base"
                >
                  {t('help_section.button')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              </motion.div>
            </motion.div>
          </div>
         </section>

         {/* Календарь для записи */}
         <section id="contact" className="py-16 sm:py-20 bg-white">
           <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
             <div className="text-center mb-8 sm:mb-12" style={{opacity: 1}}>
               <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-slate-800" style={{opacity: 1, transform: 'none'}}>
                 {t('booking.title')}
               </h2>
               <p className="text-slate-600 text-base sm:text-lg max-w-3xl mx-auto" style={{opacity: 1, transform: 'none'}}>
                 {t('booking.description')}
               </p>
             </div>
             
             {!showForm ? (
               <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
                 <div className="space-y-6" style={{opacity: 1}}>
                   <BookingCalendar onDateTimeSelect={handleDateTimeSelect} />
                 </div>

                 <div className="space-y-6 sm:space-y-8" style={{opacity: 1}}>
                   <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200 text-center" style={{opacity: 1, transform: 'none'}}>
                     <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-slate-400 mx-auto mb-4 sm:mb-6" />
                     <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-slate-800">
                       {t('contact.consultation.title')}
                     </h3>
                     <p className="text-slate-600 mb-4 sm:mb-6 text-sm sm:text-base">
                       {t('contact.consultation.description')}
                     </p>
                     <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 sm:p-4">
                       <h4 className="font-medium text-rose-800 mb-2 text-sm sm:text-base">{t('contact.expectations.title')}</h4>
                       <ul className="text-xs sm:text-sm text-rose-700 space-y-1 text-left">
                         <li>• {t('contact.expectations.consultation')}</li>
                         <li>• {t('contact.expectations.discussion')}</li>
                         <li>• {t('contact.expectations.recommendations')}</li>
                         <li>• {t('contact.expectations.questions')}</li>
                       </ul>
                     </div>
                   </div>
                 </div>
               </div>
             ) : (
               /* Форма записи */
               <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
                 {/* Левая колонка - календарь */}
                 <div className="space-y-6" style={{opacity: 1}}>
                   <BookingCalendar onDateTimeSelect={handleDateTimeSelect} />
                 </div>

                 {/* Правая колонка - форма и информация */}
                 <div className="space-y-8" style={{opacity: 1}}>
                   {/* Сообщение когда время не выбрано */}
                   {(!selectedDate || !selectedTime) && (
                     <motion.div
                       initial={{ opacity: 0, y: 20 }}
                       animate={{ opacity: 1, y: 0 }}
                       className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200 text-center"
                     >
                       <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-slate-400 mx-auto mb-6" />
                       <h3 className="text-lg sm:text-xl font-semibold mb-4 text-slate-800">
                         {t('contact.consultation.title')}
                       </h3>
                       <p className="text-slate-600 mb-6 text-sm sm:text-base">
                         {t('contact.consultation.description')}
                       </p>
                       <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 sm:p-4">
                         <h4 className="font-medium text-rose-800 mb-2 text-sm sm:text-base">{t('contact.expectations.title')}</h4>
                         <ul className="text-xs sm:text-sm text-rose-700 space-y-1 text-left">
                           <li>• {t('contact.expectations.consultation')}</li>
                           <li>• {t('contact.expectations.discussion')}</li>
                           <li>• {t('contact.expectations.recommendations')}</li>
                           <li>• {t('contact.expectations.questions')}</li>
                         </ul>
                       </div>
                     </motion.div>
                   )}

                   {/* Выбранная дата и время */}
                   {(selectedDate && selectedTime) && (
                     <motion.div
                       initial={{ opacity: 0, scale: 0.95 }}
                       animate={{ opacity: 1, scale: 1 }}
                       className="bg-primary-50 border border-primary-200 rounded-2xl p-6"
                     >
                       <h3 className="text-xl font-semibold text-primary-800 mb-4 flex items-center gap-3">
                         <Calendar className="h-6 w-6" />
                         {t('contact.selected_time')}
                       </h3>
                       <div className="space-y-2">
                         <p className="text-primary-700">
                           <span className='font-medium'>{t('contact.date')}:</span> {selectedDate}
                         </p>
                         <p className="text-primary-700">
                           <span className='font-medium'>{t('contact.time')}:</span> {selectedTime}
                         </p>
                       </div>
                     </motion.div>
                   )}

                   {/* Форма записи */}
                   <motion.div
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200"
                   >
                     <h3 className="text-lg sm:text-xl font-semibold mb-6 text-slate-800 flex items-center gap-3">
                       <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                       {t('contact.your_data')}
                     </h3>
                     
                     {!isSubmitted ? (
                       <form onSubmit={handleSubmit} className="space-y-6">
                         <div className="grid sm:grid-cols-2 gap-4">
                           <div>
                             <label className="block text-sm font-medium text-slate-700 mb-2">
                               {t('contact.form.name')} *
                             </label>
                             <input
                               type="text"
                               name="name"
                               required
                               value={formData.name}
                               onChange={handleInputChange}
                               className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                               placeholder={t('contact.form.name_placeholder')}
                             />
                           </div>
                           <div>
                             <label className="block text-sm font-medium text-slate-700 mb-2">
                               {t('contact.form.email')} *
                             </label>
                             <input
                               type="email"
                               name="email"
                               required
                               value={formData.email}
                               onChange={handleInputChange}
                               className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                               placeholder={t('contact.form.email_placeholder')}
                             />
                           </div>
                         </div>
                         
                         <div>
                           <label className="block text-sm font-medium text-slate-700 mb-2">
                             {t('contact.form.phone')}
                           </label>
                           <PhoneInput 
                             value={formData} 
                             onChange={handleInputChange}
                             placeholder={t('contact.form.phone_placeholder')}
                           />
                         </div>
                         
                         <div>
                           <label className="block text-sm font-medium text-slate-700 mb-2">
                             {t('contact.form.message')}
                           </label>
                           <textarea
                             name="message"
                             rows={4}
                             value={formData.message}
                             onChange={handleInputChange}
                             className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm sm:text-base"
                             placeholder={t('contact.form.message_placeholder')}
                           />
                         </div>
                         
                         <button
                           type="submit"
                           disabled={isSubmitting}
                           className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-sm sm:text-base"
                         >
                           {isSubmitting ? (
                             <>
                               <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                               {t('contact.form.submitting')}
                             </>
                           ) : (
                             <>
                               <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                               {t('contact.form.submit')}
                             </>
                           )}
                         </button>
                       </form>
                     ) : (
                       <motion.div
                         initial={{ opacity: 0, scale: 0.9 }}
                         animate={{ opacity: 1, scale: 1 }}
                         className="text-center py-8"
                       >
                         <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
                         <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">
                           {t('contact.form.success_title')}
                         </h3>
                         <p className="text-slate-600 text-sm sm:text-base">
                           {t('contact.form.success_message', { date: selectedDate, time: selectedTime })}
                         </p>
                       </motion.div>
                     )}
                   </motion.div>
                 </div>
               </div>
             )}
           </div>
         </section>

         {/* Призыв к действию */}
         <section className="py-16 sm:py-20 bg-white">
          <div className="container-spacious">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="text-center"
            >
              <motion.div variants={item} className="block-primary text-center">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-dark-900">
                  {t('process.cta.title')}
                </h3>
                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                  {t('process.cta.description')}
                </p>
                <a
                  href={getLocalizedPath("/contact")}
                  className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base"
                >
                  {t('process.cta.button')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
